import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { menueEintraege, MenueEintrag } from './menuEntries';
import { BereichLink, EinstLink, pfadVon } from '../pages/einstellungen/EinstLink';
import { istBereichsPfad } from './bereichsEbene';
import { zoomVon } from './Erklaerung';
import { vorladen } from '../pages/einstellungen/vorladen';
import { istEinstellungsPfad, persoenlicheAdresse, seitenTitel } from '../pages/einstellungen/seiten';
import { kontoRollenWort, kontoZeile } from '../administration/rollenWort';
import { usePlanNames } from '../administration/planNames';

// Plan 1a MA-05 (W-22): der Kontoknopf heißt offen und zu gleich; den Zustand sagt aria-expanded.
export const KONTO_KNOPF = 'Konto, Einstellungen und Firma';
// Nachbesserung I/Z-01 (VORAB-MESSUNG 7.2 Nr 3; welle-2/nachbesserung/AUFTEILUNG.md A-14, W-15): der Knopf der Seitenleiste ist
// sichtbar beschriftet – ausgeklappt eine Zeile über dem Namen, eingeklappt (auch am Handy) ein Wort unter dem Kreis. Der Name
// für Vorlesehilfen bleibt KONTO_KNOPF (Suiten und Knopfzeilen suchen ihn).
export const KONTO_LEISTE = { titel: 'Konto und Einstellungen', kurz: 'Konto' } as const;

interface UserMenuProps {
  isCollapsed?: boolean;
  // leiste: Seitenleiste der Produktfläche (öffnet nach oben); kopf: Kopfzeile der Plattformkonsole (öffnet nach unten).
  placement?: 'leiste' | 'kopf';
}

const icons: Record<string, typeof Cog6ToothIcon> = {
  einstellungen: Cog6ToothIcon, hilfe: QuestionMarkCircleIcon, anleitungen: BookOpenIcon, fehler: ChatBubbleBottomCenterTextIcon,
  'ki-cockpit': ChartBarIcon, 'admin-einstellungen': BuildingOfficeIcon, verwaltung: ServerStackIcon, abmelden: ArrowRightOnRectangleIcon,
};
const itemClass = 'w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left text-sm text-primary dark:text-white';
const unterClass = 'w-full flex items-center gap-3 pl-12 pr-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left text-sm text-primary dark:text-white';

// AE-1 Nachtrag 3 (SPEC AE-13): jede Menüform rendert dieselben Einträge aus menuEntries.ts. Profil-Nachbau F-01: zwischen
// den Blöcken steht eine Trennlinie. „Hilfe und Feedback“ ist seit PF-104 ein Link auf die Hilfe-Seite (F-06, F-19), kein
// aufklappender Knopf mehr; ein Eintrag mit unter klappte eingerückt nach unten auf (AE-F12), seit PF-104 hat keiner mehr eins.
// Das Menü öffnet keinen Meldedialog mehr. Links in die Einstellungen öffnen das Pop-up mit der aktuellen Ansicht als
// Hintergrund, Bereichsseiten (Hilfe, KI-Cockpit, Plattform-Verwaltung) öffnen als Seite; ein Eintrag mit href öffnet in einem
// neuen Fenster.
const UserMenu: React.FC<UserMenuProps> = ({ isCollapsed = false, placement = 'leiste' }) => {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [offenesUntermenue, setOffenesUntermenue] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const menuId = useId();

  // Ein geschlossenes Menü öffnet wieder mit zugeklappten Untermenüs.
  useEffect(() => { if (!isOpen) setOffenesUntermenue(null); }, [isOpen]);

  // Nachbesserung B9 (Regel 18, 200 % Zoom): das Menü der Seitenleiste bleibt im Fenster. Es öffnet über dem Knopf; passt
  // es dort nicht und ist darunter mehr Platz, öffnet es darunter. Höher als der Platz wird es nicht, es scrollt in sich.
  const [lage, setLage] = useState<{ unten: boolean; hoehe: number } | null>(null);
  useLayoutEffect(() => {
    if (!isOpen || placement === 'kopf') { setLage(null); return; }
    const knopf = triggerRef.current?.getBoundingClientRect(), menue = navRef.current;
    if (!knopf || !menue) return;
    // Bildschirm-px in px des Menüs (F-27: die App ab 800 px mit zoom).
    const z = zoomVon(menue);
    const oben = ((isCollapsed ? knopf.bottom : knopf.top) - 8) / z, unten = (window.innerHeight - (isCollapsed ? knopf.top : knopf.bottom) - 8) / z;
    setLage(menue.scrollHeight <= oben || oben >= unten ? { unten: false, hoehe: Math.floor(oben) } : { unten: true, hoehe: Math.floor(unten) });
  }, [isOpen, offenesUntermenue, isCollapsed, placement]);
  const lageStil: React.CSSProperties | undefined = lage ? { maxHeight: lage.hoehe, overflowY: 'auto' } : undefined;

  const closeOnEscape = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (!user) return '?';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.email[0].toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'Benutzer';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  // Plan 1a MA-05 (W-23): unter dem Namen steht, wo man hingehört - „Talblick Software GmbH · Besitzer“, beim Einzelkonto
  // „Tarif Start“, beim Plattformkonto und in einer Sicht die E-Mail-Adresse. Die Rolle kommt nur aus account.rolle
  // (kontoRollenWort), nie aus einem Rollennamen (VE-02 (b)); in einer Sicht wäre access die fremde Firma.
  const planName = usePlanNames();
  const zeile = user ? kontoZeile({ email: user.email, sicht: !!user.sicht?.aktiv, organisation: user.access?.organization,
    rolle: user.account?.rolle, planName: user.access?.plan ? planName(user.access.plan) : null }, brand) : '';
  const titel = user ? `${getUserDisplayName()} · ${zeile}` : getUserDisplayName();
  // Plan 1a Welle 2, UE-06 Teil 2 (UP-12; Mira: „weil ich ‚Mitglied‘ bei mir sehe, aber noch nicht weiß, was das heißt“):
  // nennt die Zeile Firma und Rolle, führt sie im Menükopf als Link zu „Mein Zugang“. Beim Einzelkonto („Tarif Start“), beim
  // Plattformkonto und in einer Sicht (E-Mail-Adresse) bleibt sie Text. Verglichen wird die Zeile selbst, kein Rollenname.
  const firmaDerZeile = user && !user.sicht?.aktiv ? user.access?.organization : null;
  const rolleDerZeile = kontoRollenWort(user?.account?.rolle, brand);
  const zeileNenntFirmaUndRolle = !!firmaDerZeile && !!rolleDerZeile && zeile === `${firmaDerZeile.name} · ${rolleDerZeile}`;
  const zugangLinkName = `${zeile}: ${seitenTitel('zugang')} öffnen`;

  // PF-104: die Rechtslinks stehen auf /hilfe/dokumente, das Menü bekommt sie nicht mehr.
  const eintraege = menueEintraege({ ebenen: user?.ebenen, produkt: brand.displayName });
  const handle = (eintrag: MenueEintrag) => {
    switch (eintrag.aktion) {
      case 'abmelden': void handleLogout(); return;
    }
  };
  const render = (eintrag: MenueEintrag, unter = false): React.ReactNode => {
    const Icon = icons[eintrag.key];
    const klasse = unter ? unterClass : itemClass;
    // F-23 (PF-NB-verwaltung-6): die Zusatzzeile (eigene Firma, „Alle Kundenfirmen, Kosten und Support“) ist Beschreibung, nicht
    // Teil des Namens: aria-hidden in der Zeile, aria-describedby am Eintrag; der zugängliche Name bleibt genau label.
    const zusatzId = eintrag.zusatz ? `${menuId}-${eintrag.key}-zusatz` : undefined;
    const inhalt = <>{Icon && <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />}<span className="flex-1">{eintrag.label}
      {eintrag.zusatz && <small id={zusatzId} aria-hidden="true" className="block text-xs text-muted">{eintrag.zusatz}</small>}</span>
      {eintrag.extern && <ArrowTopRightOnSquareIcon className="w-4 h-4 flex-shrink-0" aria-hidden />}</>;
    if (eintrag.unter) {
      const offen = offenesUntermenue === eintrag.key;
      const listeId = `${menuId}-${eintrag.key}`;
      const Pfeil = offen ? ChevronUpIcon : ChevronDownIcon;
      return <div key={eintrag.key}>
        <button type="button" aria-expanded={offen} aria-controls={listeId} onClick={() => setOffenesUntermenue(offen ? null : eintrag.key)} className={itemClass}>
          {inhalt}<Pfeil className="w-4 h-4 flex-shrink-0 text-muted" aria-hidden />
        </button>
        <div id={listeId} hidden={!offen}>{eintrag.unter.map(u => render(u, true))}</div>
      </div>;
    }
    // PF-105 (Link-Regel): Bereichspfade (istBereichsPfad, etwa /ki-cockpit und /verwaltung, seit PF-104 auch /hilfe) über
    // BereichLink, damit die Herkunft für „Zurück zum Chat“ mitgeht; übrige Ziele unter /einstellungen über EinstLink, alles
    // andere über Link.
    if (eintrag.to && istBereichsPfad(pfadVon(eintrag.to))) return <BereichLink key={eintrag.key} to={eintrag.to} onClick={() => setIsOpen(false)} className={klasse} aria-describedby={zusatzId}>{inhalt}</BereichLink>;
    if (eintrag.to && !istEinstellungsPfad(pfadVon(eintrag.to))) return <Link key={eintrag.key} to={eintrag.to} onClick={() => setIsOpen(false)} className={klasse} aria-describedby={zusatzId}>{inhalt}</Link>;
    if (eintrag.to) return <EinstLink key={eintrag.key} to={eintrag.to} onClick={() => setIsOpen(false)} className={klasse} aria-describedby={zusatzId}>{inhalt}</EinstLink>;
    if (eintrag.href) return <a key={eintrag.key} href={eintrag.href} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className={klasse} aria-describedby={zusatzId}>{inhalt}</a>;
    return <button key={eintrag.key} type="button" onClick={() => handle(eintrag)} className={klasse} aria-describedby={zusatzId}>{inhalt}</button>;
  };
  // F-01: eine Trennlinie, wo der Block wechselt (Vorbild meinGPT).
  const menu = <>{eintraege.map((eintrag, i) => <React.Fragment key={eintrag.key}>
    {i > 0 && eintrag.block !== eintraege[i - 1].block && <hr className="my-1 border-warm-200 dark:border-gray-600" />}
    {render(eintrag)}
  </React.Fragment>)}</>;
  // Nachbesserung B12 (b): verlässt der Fokus das offene Menü (Tab, Umschalt+Tab), schließt es sich. Ohne neues Ziel
  // (Klick auf eine nicht fokussierbare Stelle im Menü) bleibt es offen; den Klick daneben fängt handleClickOutside.
  const fokusVerlassen = (event: React.FocusEvent) => {
    const ziel = event.relatedTarget as Node | null;
    if (isOpen && ziel && !menuRef.current?.contains(ziel)) setIsOpen(false);
  };
  const trigger = (children: React.ReactNode, className: string) => (
    <button ref={triggerRef} type="button" aria-label={KONTO_KNOPF} aria-expanded={isOpen} aria-controls={menuId} onClick={() => { if (!isOpen) vorladen(); setIsOpen(!isOpen); }} className={className} title={titel}>{children}</button>
  );
  // Kopf des Menüs: Name, Firma · Rolle, E-Mail-Adresse; ist die Zeile schon die E-Mail-Adresse, steht sie nur einmal.
  // Schlusslauf 2, M1 (Regel 18): Firma · Rolle bricht um statt gekürzt zu werden, damit das Rollenwort (MA-05) zu lesen ist.
  // UP-12: mit Firma und Rolle ist die Zeile ein Link auf „Mein Zugang“ – derselbe Text, unterstrichen wie die Textlinks im
  // Pop-up, mindestens 44 px hoch, für Vorlesehilfen „{Zeile}: Mein Zugang öffnen“; der Klick schließt das Menü. Den Kopf gibt
  // es in der eingeklappten Leiste (auch am Handy) und in der Kopfzeile der Betreiber-Verwaltung; in der ausgeklappten Leiste
  // steht die Zeile im Knopf und bleibt Text (ein Link in einem Knopf wäre kein gültiges HTML).
  const kopfzeile = <div className="px-4 py-3 border-b border-warm-200 dark:border-gray-600"><strong className="block text-sm text-primary dark:text-white truncate">{getUserDisplayName()}</strong>
    {zeile && zeile !== user?.email && (zeileNenntFirmaUndRolle
      ? <EinstLink to={persoenlicheAdresse('zugang')} onClick={() => setIsOpen(false)} aria-label={zugangLinkName} className="flex items-center min-h-[44px] text-sm text-muted break-words underline underline-offset-[3px] hover:text-primary dark:hover:text-white">{zeile}</EinstLink>
      : <span className="block text-sm text-muted break-words">{zeile}</span>)}
    <span className="block text-sm text-muted truncate">{user?.email}</span></div>;

  if (placement === 'kopf') {
    return (
      <div ref={menuRef} className="relative user-menu-kopf" onKeyDown={closeOnEscape} onBlur={fokusVerlassen}>
        {trigger(<><span className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">{getUserInitials()}</span><span className="user-menu-kopf-name">{getUserDisplayName()}</span></>, 'adm-button user-menu-kopf-trigger')}
        {isOpen && (
          <nav id={menuId} aria-label="Benutzerprofil" className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] max-h-[calc(100vh-5rem)] overflow-y-auto bg-white dark:bg-gray-700 border border-warm-200 dark:border-gray-600 rounded-xl shadow-lg z-50 text-left">
            {kopfzeile}{menu}
          </nav>
        )}
      </div>
    );
  }

  if (isCollapsed) {
    return (
      <div ref={menuRef} className="relative" onKeyDown={closeOnEscape} onBlur={fokusVerlassen}>
        {trigger(<div className="flex flex-col items-center gap-1"><div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">{getUserInitials()}</div><span className="text-sm text-muted">{KONTO_LEISTE.kurz}</span></div>, 'p-2 hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors')}
        {isOpen && (
          <nav ref={navRef} id={menuId} aria-label="Benutzerprofil" style={lageStil} className={`editorial-compact-menu absolute ${lage?.unten ? 'top-0' : 'bottom-0'} left-full ml-2 w-64 max-h-[calc(100vh-2rem)] overflow-y-auto bg-white dark:bg-gray-700 border border-warm-200 dark:border-gray-600 shadow-lg z-50`}>
            {kopfzeile}{menu}
          </nav>
        )}
      </div>
    );
  }

  // Schlusslauf 3, zweiter Lauf: ist die Zeile die E-Mail-Adresse, trägt sie user-menu-email. Nur die Leiste der Verwaltung
  // (verwaltung-rahmen.css) hält sie damit einzeilig; „Firma · Rolle“ bekommt die Klasse nie, auch nicht bei einem Konto ohne Namen.
  return (
    <div ref={menuRef} className="editorial-user-menu relative" onKeyDown={closeOnEscape} onBlur={fokusVerlassen}>
      {trigger(<>
        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
          {getUserInitials()}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm text-muted">{KONTO_LEISTE.titel}</div>
          <div className="text-sm font-medium text-primary dark:text-white truncate">
            {getUserDisplayName()}
          </div>
          <div className={zeile === user?.email ? 'text-sm text-muted break-words user-menu-email' : 'text-sm text-muted break-words'}>{zeile}</div>
        </div>
        <ChevronUpIcon className={`w-4 h-4 text-muted transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </>, 'w-full flex items-center gap-3 p-3 hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors')}

      {isOpen && (
        <nav ref={navRef} id={menuId} aria-label="Benutzerprofil" style={lageStil} className={`absolute ${lage?.unten ? 'top-full mt-1' : 'bottom-full mb-1'} left-0 right-0 max-h-[calc(100vh-6rem)] overflow-y-auto bg-white dark:bg-gray-700 border border-[rgb(var(--cl-ink-rgb)/0.15)] dark:border-gray-600 rounded-xl shadow-lg z-50`}>
          {menu}
        </nav>
      )}
    </div>
  );
};

export default UserMenu;
